

import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useStore } from '../../context/StoreContext';
import { useCart } from '../../context/CartContext';
import { useToast } from '../../context/ToastContext';
import { ArrowLeft, Minus, Plus, ShoppingBag, Check, Star, Info, Lock } from 'lucide-react';
import { CartItem, CartItemOption } from '../../types';
import { CustomerLayout } from '../../components/ui/Layout';

const ProductDetail: React.FC = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { products, restaurant, isLoading } = useStore();
  const { addToCart } = useCart();
  const { addToast } = useToast();

  const product = products.find(p => p.id === productId);
  const [quantity, setQuantity] = useState(1);
  const [selectedOptions, setSelectedOptions] = useState<CartItemOption[]>([]);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (product && restaurant) {
      // FIX: Property 'name' does not exist on type 'InstagramProfile'. Use 'username' instead.
      document.title = `${product.name} | ${restaurant.username}`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute('content', `${product.name}: ${product.description || 'Peça agora este item delicioso em nosso cardápio digital.'} Preço: ${restaurant.currency} ${product.price.toFixed(2)}`);
      
      // JSON-LD para Produto
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify({
        "@context": "https://schema.org/",
        "@type": "Product",
        "name": product.name,
        "image": product.imageUrl,
        "description": product.description,
        "brand": {
          "@type": "Brand",
          // FIX: Property 'name' does not exist on type 'InstagramProfile'. Use 'username' instead.
          "name": restaurant.username
        },
        "offers": {
          "@type": "Offer",
          "url": window.location.href,
          "priceCurrency": "BRL",
          "price": product.price,
          "availability": product.inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock"
        }
      });
      document.head.appendChild(script);
      return () => { document.head.removeChild(script); };
    }
  }, [product, restaurant]);

  const missingRequiredGroups = useMemo(() => {
    if (!product) return [];
    return product.groups
      .filter(g => g.required)
      .filter(g => !selectedOptions.some(o => o.groupId === g.id));
  }, [product, selectedOptions]);

  const handleOptionToggle = (groupId: string, option: {id: string, name: string, price: number}, isMulti: boolean, max: number) => {
    setSelectedOptions(prev => {
      let next = [...prev];
      const existingInGroup = next.filter(o => o.groupId === groupId);
      const existingIdx = next.findIndex(o => o.optionId === option.id);
      
      if (existingIdx > -1) {
         next.splice(existingIdx, 1);
      } else {
         if (!isMulti) {
            next = next.filter(o => o.groupId !== groupId);
         } else if (existingInGroup.length >= max) {
            const firstOfGroup = next.findIndex(o => o.groupId === groupId);
            if (firstOfGroup > -1) next.splice(firstOfGroup, 1);
         }
         next.push({ groupId, optionId: option.id, name: option.name, price: option.price, quantity: 1 });
      }
      return next;
    });
  };

  const calculateTotal = () => {
    if (!product) return 0;
    const optionsTotal = selectedOptions.reduce((sum, opt) => sum + opt.price, 0);
    return Number(((product.price + optionsTotal) * quantity).toFixed(2));
  };

  const handleAddToCart = () => {
    if (!product || !restaurant) return;
    if (!restaurant.isOpen) {
      addToast('Cozinha pausada no momento.', 'error');
      return;
    }
    if (missingRequiredGroups.length > 0) {
      addToast(`Oops! Escolha o(a) ${missingRequiredGroups[0].name}`, 'error');
      return;
    }
    const item: CartItem = {
      internalId: `cart_${Date.now()}`,
      productId: product.id,
      productName: product.name,
      basePrice: product.price,
      quantity,
      imageUrl: product.imageUrl,
      selectedOptions,
      notes: notes.trim(),
      totalPrice: calculateTotal()
    };
    addToCart(item);
    addToast(`${quantity}x ${product.name} na sacola!`, 'success');
    navigate(-1);
  };

  if (isLoading || !product || !restaurant) return <div className="min-h-screen bg-white animate-pulse"></div>;

  const isClosed = !restaurant.isOpen;
  const primaryColor = restaurant.styles?.primaryColor || '#f43f5e';

  return (
    <CustomerLayout showCart={false}>
      <article className="min-h-screen bg-gray-50 text-gray-900 pb-48 animate-fade-in">
        <div className="relative h-72 md:h-[450px] w-full overflow-hidden bg-gray-900">
          <img src={product.imageUrl} className="w-full h-full object-cover opacity-80" alt={`Foto detalhada de ${product.name}`} />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-50 via-transparent to-black/40"></div>
          <button 
            onClick={() => navigate(-1)}
            className="absolute top-8 left-8 w-12 h-12 bg-white/10 backdrop-blur-xl rounded-full flex items-center justify-center text-white border border-white/20 hover:bg-white hover:text-black transition-all shadow-2xl z-20"
            aria-label="Voltar para o cardápio"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
        </div>

        <main className="max-w-4xl mx-auto -mt-20 relative z-10 px-6">
          <div className="bg-white rounded-[3rem] shadow-premium p-8 md:p-12 space-y-10 border border-gray-100">
            <header className="space-y-6">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <h1 className="text-4xl md:text-5xl font-black text-gray-900 tracking-tighter uppercase italic">{product.name}</h1>
                <div className="bg-rose-500/5 px-6 py-3 rounded-2xl border border-rose-500/10">
                   <span className="text-3xl font-black text-rose-500" style={{ color: primaryColor }}>
                     {restaurant.currency} {product.price.toFixed(2).replace('.', ',')}
                   </span>
                </div>
              </div>
              <p className="text-gray-500 text-lg font-medium leading-relaxed max-w-2xl">{product.description}</p>
              
              <div className="flex items-center gap-6 py-6 border-y border-gray-50">
                 <div className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-gray-400">
                    <Star className="w-4 h-4 text-orange-400 fill-orange-400" /> 4.9 <span className="opacity-40 italic">(Review Real)</span>
                 </div>
                 {isClosed && (
                   <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-red-500 bg-red-50 px-4 py-2 rounded-full border border-red-100 animate-pulse">
                     <Lock className="w-4 h-4" /> Cozinha Pausada
                   </div>
                 )}
              </div>
            </header>

            <div className="space-y-16">
              {product.groups.map(group => (
                <section key={group.id} className="space-y-8">
                   <div className="flex justify-between items-end border-l-4 border-rose-500 pl-4">
                      <div>
                        <h2 className="font-black text-gray-900 uppercase tracking-widest text-sm">{group.name}</h2>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">Selecione até {group.max} itens</p>
                      </div>
                      {group.required && <span className="text-[9px] font-black text-rose-500 bg-rose-50 px-4 py-2 rounded-xl border border-rose-100 uppercase tracking-widest">Obrigatório</span>}
                   </div>
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {group.options.map(option => {
                         const isSelected = selectedOptions.some(o => o.optionId === option.id);
                         return (
                           <button 
                             key={option.id}
                             disabled={isClosed}
                             onClick={() => handleOptionToggle(group.id, option, group.max > 1, group.max)}
                             className={`w-full flex items-center justify-between p-6 rounded-[2rem] border-2 transition-all text-left group/opt ${isSelected ? 'border-rose-500 bg-rose-500/5 shadow-premium scale-[1.02]' : 'border-gray-50 bg-gray-50/30 hover:bg-gray-100 hover:border-gray-200'}`}
                           >
                              <div className="flex items-center gap-5">
                                 <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${isSelected ? 'bg-rose-500 border-rose-500 rotate-12 shadow-glow-rose' : 'border-gray-300 bg-white'}`} style={{ backgroundColor: isSelected ? primaryColor : '', borderColor: isSelected ? primaryColor : '' }}>
                                    {isSelected && <Check className="w-4 h-4 text-white" />}
                                 </div>
                                 <span className={`font-bold transition-colors ${isSelected ? 'text-gray-900' : 'text-gray-500'}`}>{option.name}</span>
                              </div>
                              {option.price > 0 && <span className={`font-black text-xs ${isSelected ? 'text-rose-600' : 'text-gray-400'}`}>+ {restaurant.currency} {option.price.toFixed(2)}</span>}
                           </button>
                         );
                      })}
                   </div>
                </section>
              ))}

              <div className="space-y-6">
                <h2 className="font-black text-gray-900 uppercase tracking-widest text-sm flex items-center gap-3">
                  <Info className="w-5 h-5 text-gray-400" /> Algum pedido especial?
                </h2>
                <textarea 
                  value={notes}
                  disabled={isClosed}
                  onChange={e => setNotes(e.target.value)}
                  placeholder="Ex: Tirar a cebola, enviar molho extra..."
                  className="w-full bg-white border border-gray-100 rounded-[2rem] p-8 text-gray-700 outline-none focus:ring-4 focus:ring-rose-500/5 transition-all h-40 resize-none text-base font-medium shadow-inner"
                  aria-label="Notas para o restaurante"
                />
              </div>
            </div>
          </div>
        </main>

        {!isClosed && (
          <div className="fixed bottom-8 left-6 right-6 z-50 animate-slide-up">
            <div className="max-w-4xl mx-auto bg-gray-900 rounded-[2.5rem] p-4 flex items-center gap-6 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.3)] border border-white/10">
               <div className="flex items-center bg-white/5 rounded-2xl p-1 h-16 border border-white/5">
                  <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-14 h-14 rounded-xl flex items-center justify-center text-white hover:bg-white/10 transition-all" aria-label="Diminuir quantidade"><Minus className="w-5 h-5" /></button>
                  <span className="w-12 text-center font-black text-xl text-white">{quantity}</span>
                  <button onClick={() => setQuantity(quantity + 1)} className="w-14 h-14 rounded-xl flex items-center justify-center text-white hover:bg-white/10 transition-all" aria-label="Aumentar quantidade"><Plus className="w-5 h-5" /></button>
               </div>
               
               <button 
                 onClick={handleAddToCart}
                 disabled={missingRequiredGroups.length > 0}
                 className={`flex-1 h-16 rounded-2xl font-black text-white flex justify-between items-center px-10 transition-all shadow-xl active:scale-95
                 ${missingRequiredGroups.length > 0 
                   ? 'bg-gray-800 text-gray-500 cursor-not-allowed border border-white/5' 
                   : 'bg-rose-500 hover:bg-rose-600 shadow-glow-rose'}`}
                 style={{ backgroundColor: missingRequiredGroups.length > 0 ? '' : primaryColor }}
               >
                  <span className="uppercase text-xs tracking-[0.2em] flex items-center gap-4">
                     {missingRequiredGroups.length > 0 ? 'Faltam Itens' : 'Adicionar'} <ShoppingBag className="w-5 h-5" />
                  </span>
                  <span className="bg-black/20 px-6 py-2 rounded-xl text-sm italic">{restaurant.currency} {calculateTotal().toFixed(2).replace('.', ',')}</span>
               </button>
            </div>
          </div>
        )}
      </article>
    </CustomerLayout>
  );
};

export default ProductDetail;